import template from './bBarChartWithLegend.html';
import moment from 'moment';

const COLOR_NVA = '#FF644B';
const COLOR_VA = '#1C3828';

export function bBarChartWithLegendDirective() {
  'ngInject';
  return {
    restrict: 'E',
    template: template,
    controller: bBarChartWithLegendController,
    controllerAs: 'bBarChartWithLegend',
    bindToController: true,
    scope: {
      tooltipId: "@",
      type: "@",
      data: "=",
      canShare: "=",
      processGroup: "=",
      onShareChart: "&",
      onExportChart: "&"
    }
  };
}

class bBarChartWithLegendController {
  constructor($filter, $scope, $translate, $rootScope) {
    'ngInject';
    this.$onInit = () => {
      this.$scope = $scope;
      this.$rootScope = $rootScope;
      this.$translate = $translate;
      this.$filter = $filter;
      this.showTooltip = false;
      this.isVa = this.type === 'va';
      this.color = this.isVa ? COLOR_VA : COLOR_NVA;
      this.key = this.isVa ? this.$translate.instant('STATS.VA') : this.$translate.instant('STATS.NVA');
      this.title = `${this.key} ` + this.$translate.instant('STATS.PROCESSES');

      this.field = this.isVa ? 'vaDuration' : 'nvaDuration';
      this.parsedData = [];
      this.labelData = [];
      this.chartData = [];
      this.maxYValue = 0;
      this.updateChartOptions();
      this.$scope.$watch(() => this.data, newValue => this.updateChart(newValue));
      this.$scope.$watch(() => this.processGroup, () => this.updateChart(this.data));
    };
  }

  exportChart() {
    this.$rootScope.exportProcessGroupVaAndNva = this.data;
    this.onExportChart({exportData: {type: this.isVa ? 'VaProcesses' : 'NvaProcesses'}});
  }

  getCharIdAt(i) {
    return (i >= 26 ? this.getCharIdAt((i / 26 >> 0) - 1) : '') + 'abcdefghijklmnopqrstuvwxyz'[i % 26 >> 0];
  }

  getMaxValue(data) {
    if (!data || !data.length) {
      return 0;
    }

    const hoursCeil = Math.ceil(moment.duration(data[0][this.field]).asHours());
    return hoursCeil * 1000 * 3600;
  }

  getRectTooltipPosition(node) {
    const bcr = node.getBoundingClientRect();
    return {top: bcr.top - 38, left: bcr.left - 30};
  }

  shareChart() {
    const shareData = {type: this.isVa ? 'va_processes' : 'nva_processes'};
    this.onShareChart({shareData});
  }

  updateChartOptions() {
    const tooltipId = this.tooltipId;
    const chartSizeConsts = {
      xTickPadding: 28,
      yTickPadding: 30,
      xLabelHeight: 20,
      chartHeight: 280,
      chartTopMargin: 10,
      charContainerMargin: 20
    };

    this.maxYValue = this.getMaxValue(this.parsedData);
    this.chartOptions = {
      chart: {
        type: 'multiBarChart',
        noData: this.$translate.instant('NOTHING_TO_SHOW'),
        height: chartSizeConsts.chartHeight,
        reduceXTicks: false,
        stacked: false,
        showLegend: false,
        showControls: false,
        x: d => d.label,
        y: d => d.value,
        yDomain: [0, this.maxYValue ? this.maxYValue : 1],
        margin: {
          left: 75,
          right: 60,
          top: chartSizeConsts.chartTopMargin,
          bottom: chartSizeConsts.xLabelHeight + chartSizeConsts.xTickPadding
        },
        xAxis: {
          tickPadding: chartSizeConsts.xTickPadding,
          tickFormat: (d, i) => {
            return this.parsedData[i].name !== null
              ? this.labelData.length === 1 ? 'A' : this.getCharIdAt(i).toUpperCase()
              : '';
          }
        },
        yAxis: {
          showMaxMin: true,
          tickPadding: chartSizeConsts.yTickPadding,
          tickValues: d => {
            const max = moment.duration(this.maxYValue).asHours();
            const ticksCount = Math.min(d[0].values.length, 6);
            let tickVal = parseInt(max / ticksCount);
            tickVal = tickVal ? tickVal : 1;
            const tickVals = [0];
            if (max === 0 || tickVal === 0) {
              return [0];
            }

            for (let t = tickVal; t < max; t += tickVal) {
              tickVals.push(t);
            }
            return tickVals.map(val => val * 1000 * 3600);
          },
          tickFormat: d => {
            if (d < 0 || (d > 0 && d < 1000)) {
              return '';
            }
            return `${moment.duration(d).asHours()} h`;
          }
        },
        tooltip: {
          position: () => {
            const tooltipEl = angular.element(document.getElementById(`view-360-tooltip-${tooltipId}`));
            const parent = tooltipEl.parent();
            if (tooltipEl && parent && !parent.hasClass('view-360-tooltip')) {
              parent.addClass('view-360-tooltip');
            }

            const rect = angular.element(document.getElementsByClassName('hover'));
            if (!rect || !rect.length) {
              return {};
            }

            return this.getRectTooltipPosition(rect[0]);
          },
          contentGenerator: function (x) {
            const duration = moment.duration(x.data.value);
            let hours = parseInt(duration.asHours());
            let minutes = duration.minutes();
            hours = hours < 10 ? `0${hours}` : hours;
            minutes = minutes < 10 ? `0${minutes}` : minutes;
            return `<div id="view-360-tooltip-${tooltipId}" class="view-360-tooltip-inner">
                    <div>${x.data.name}</div>
                    <div>${hours}:${minutes}</div>
             </div>`;
          }
        },
      }
    };

    if(this.type === "va"){
      this.$rootScope.chartDataVa = angular.copy(this.parsedData);
    }

    if(this.type === "nva"){
      this.$rootScope.chartDataNva = angular.copy(this.parsedData);
    }

  }

  updateChart(newValue) {
    if (!newValue || !this.type) {
      return;
    }

    const data = newValue;
    if (data.length === 1) {
      data.push({
        name: null,
        vaDuration: NaN,
        nvaDuration: NaN
      });
      data.unshift({
        name: null,
        vaDuration: -NaN,
        nvaDuration: -NaN
      });
    }

    const processGroup = this.processGroup.toLowerCase();
    this.$rootScope.processGroupChartWithLegend = processGroup;

    this.parsedData = data.filter( item => {
      return this.processGroup ? item.processGroupName.toLowerCase() === processGroup : item;
    }).sort((a, b) => {
      const first = moment.duration(a[this.field]).asMilliseconds();
      const last = moment.duration(b[this.field]).asMilliseconds();
      return last - first;
    }).slice(0, 15);

    this.labelData = this.parsedData.map(x => x.name);
    this.maxYValue = this.getMaxValue(this.parsedData);
    this.chartData = [
      {
        color: this.color,
        key: this.key,
        values: this.parsedData.map((process, idx) => {
          return {
            value: moment.duration(process[this.field]).asMilliseconds(),
            label: idx,
            name: process.name
          };
        })
      }
    ];
    this.updateChartOptions();
  }
}
